import { useEffect, useState } from "react";
import { favoriteStorage } from "../../data/storage";
import type { ActionDef, ColumnDef } from "@/client/components/table/Tablel";
import Tablel from "@/client/components/table/Tablel";
import { getItemIconUrl } from "@/client/util/wowhead";
import { useAuctionHouseCtx } from "@/client/contexts/AuctionHouseProvider";
import ItemCurrent from "../item_page/ItemCurrent";
import { LoadingIcon } from "@/client/components/LoadingIcon";
import HistoryChart from "../../components/history_chart/HistoryChart";
import "./favoritePage.css";
import { Link } from "react-router-dom";
import { getOrLoadItem } from "@/client/data/ItemCache";

type FavTableData = {
    itemId: number;
    itemName: string;
    quality: number;
    icon: string;
    data: ApiAuctionItemData[];
    avgPct: number;
    state: "loading" | "loaded" | "notloaded" | "error";
    error?: string;
};

function lastBuyoutOrZero(data: ApiAuctionItemData[]): number {
    const lastEntry = data[data.length - 1];
    return lastEntry ? lastEntry.minBuyout : 0;
}

const columDefs: ColumnDef<FavTableData>[] = [
    {
        name: "Item",
        dataKey: "itemName",
        canSort: true,
        defaultSort: "desc",
        render: (rd) => (
            <Link className="favTableItemLink" to={`/item/${rd.itemId}`}>
                <img className="itemIcon favTableItemIcon" src={getItemIconUrl(rd.icon, "large")}></img>
                <span className={`itemQuality${rd.quality}`}>{rd.itemName}</span>
            </Link>
        ),
    },
    {
        name: "Current Price",
        dataKey: "data",
        canSort: true,
        customSort: (a, b) => lastBuyoutOrZero(a.data) - lastBuyoutOrZero(b.data),
        render: (rd) => {
            switch (rd.state) {
                case "loaded":
                    return <ItemCurrent data={rd.data[rd.data.length - 1]} />;
                case "error":
                    return <>Error: {rd.error ?? "Unknown error"}</>;
                default:
                    return <LoadingIcon />;
            }
        },
    },
    {
        name: "Avg. %",
        dataKey: "avgPct",
        canSort: true,
        render: (rd) => (rd.state == "loaded" ? Math.round(rd.avgPct * 1000) / 10 + " %" : ""),
    },
    {
        name: "History",
        dataKey: "data",
        render: (rd) =>
            rd.state == "loaded" ? (
                <div className="favTableChartTd">
                    <HistoryChart data={rd.data} />
                </div>
            ) : (
                ""
            ),
    },
];

const actionDefs: ActionDef<FavTableData>[] = [
    {
        name: "Remove",
        customCssClasses: ["button", "favRemoveBtn"],
        onClick: (rd) => {
            const favData = favoriteStorage.getEntry("favs");
            const favIdx = favData.findIndex((v) => v.itemId == rd.itemId);
            if (favIdx !== -1) {
                favData.splice(favIdx, 1);
                favoriteStorage.setEntry("favs", favData);
            }
        },
    },
];

function ftHasEntry(ft: FavTableData[], itemId: number): number {
    return ft.findIndex((v) => v.itemId == itemId);
}

const FavoritePage = (): JSX.Element => {
    const { selectedAuctionHouse } = useAuctionHouseCtx();
    const [tableData, setTableData] = useState<FavTableData[]>();

    const updateTableData = (newTd: FavTableData[]) => {
        setTableData(newTd);
        if (selectedAuctionHouse) {
            for (const entry of newTd) {
                if (entry.state != "notloaded") continue;
                getOrLoadItem(selectedAuctionHouse, entry.itemId)
                    .then((item) => {
                        if (!item) {
                            entry.state = "error";
                            entry.error = "Item not found!";
                        } else {
                            let total = 0;

                            for (const v of item.data) {
                                total += v.minBuyout;
                            }

                            entry.data = item.data;
                            const lastEntry = item.data[item.data.length - 1];
                            if (total > 0 && lastEntry) entry.avgPct = lastEntry.minBuyout / (total / item.data.length);
                            entry.state = "loaded";
                        }
                        setTableData([...newTd]);
                    })
                    .catch((err) => {
                        entry.state = "error";
                        entry.error = err instanceof Error ? err.message : String(err);
                    });
            }
        }
    };

    useEffect(() => {
        const onFavChange = () => {
            const favorites = favoriteStorage.getEntry("favs");
            const newData = tableData ? [...tableData] : [];
            for (const fav of favorites) {
                if (ftHasEntry(newData, fav.itemId) !== -1) continue;
                newData.push({
                    itemId: fav.itemId,
                    itemName: fav.itemName,
                    quality: fav.qualityId,
                    icon: fav.iconName,
                    data: [],
                    avgPct: 0,
                    state: "notloaded",
                });
            }
            updateTableData(newData);
        };

        favoriteStorage.addOnChange(onFavChange);
        onFavChange();

        return () => {
            favoriteStorage.removeOnChange(onFavChange);
        };
    }, []);

    useEffect(() => {
        if (!tableData) return;
        const newTd = [...tableData];
        for (const td of newTd) td.state = "notloaded";
        updateTableData(newTd);
    }, [selectedAuctionHouse]);

    return (
        <>
            {tableData && tableData.length > 0 ? (
                <Tablel
                    columnDefs={columDefs}
                    data={tableData ?? []}
                    actions={actionDefs}
                    sortCol="itemName"
                    sortDir="asc"
                    customCssClasses={{
                        table: "table favTable",
                        th: "favTableTh",
                        tr: "favTableTr",
                        td: "favTableTd",
                    }}
                ></Tablel>
            ) : (
                <span className="favPageNone">No favorites set!</span>
            )}
        </>
    );
};

export default FavoritePage;
